import { useCallback, useEffect, useRef, useState } from "react";
import StudentClassContext from "src/context/studentClassContext";
import useRegularClass from "src/hooks/useRegularClass";
import useNav from "src/hooks/useNav";
import createArrayFromSize from "src/utils/creatArrayFromSize";
import deepCopy from "src/utils/deepCopy";
import { dateFormat } from "src/utils/formatdate";
import generateClassFormListInfo from "src/utils/generateClassFormListInfo";
import subtractMonths from "src/utils/subtractMonths";
import { swalError } from "src/utils/swalHelpers";
import useTuitionFees from "src/hooks/useTuitionFees";
import { maxAllowedClasses } from "src/utils/constants";
import { usePostMemberClassesAssignMutation } from "src/services/react-query-apis/post-member-classes-assign";
import { toast } from "react-toastify";
import moment from "moment";

const StudentClassProvider = ({ children, ...props }) => {
  const regularClass = useRegularClass();
  const tuitionFees = useTuitionFees();
  const { currentMonth } = useNav();

  const { memberData, readOnly, setReadOnly } = props;

  const tableFilterButtonRef = useRef(null);

  const [classList, setClassList] = useState([]);
  const [formList, setFormList] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [billingAmount, setBillingAmount] = useState(null);

  // note praveen keep this in sync with other states!
  const [date, setDate] = useState(currentMonth);

  let formData = formList.find(val => val.id === selectedClassId);
  formData = formData ? deepCopy(formData) : {};

  const disableForm =
    props.readOnly ||
    !formList[0]?.id ||
    !regularClass.isLoaded ||
    !tuitionFees.isLoaded;

  const getFormFees = useCallback(
    formData => {
      const { closed_flag, times, regular_class_id, processed_date } = formData;

      if (
        memberData.leave_date &&
        moment(memberData.leave_date).isSameOrBefore(
          moment(`${processed_date}-01`),
          "month"
        )
      ) {
        return 0;
      }

      const tuition = tuitionFees.list.find(
        fee => fee.regular_class_id === regular_class_id && fee.times === times
      );

      if (!tuition) {
        console.warn(
          "Missing fee data for class/times. This should not happen."
        );
        return 0;
      }

      const chargeTypeCode = memberData.charge_type_code;

      if (chargeTypeCode === "無料") {
        return 0;
      }

      const isClosed = closed_flag !== 0;

      const fee = isClosed
        ? (tuition.closure_fee ?? 0)
        : ({
            特別１: tuition.special_fee_1 ?? 0,
            特別２: tuition.special_fee_2 ?? 0,
            通常: tuition.tution_fee ?? 0,
          }[chargeTypeCode] ?? 0);

      return fee;
    },
    [tuitionFees.list, memberData.charge_type_code, memberData.leave_date]
  );

  const getTotalFees = useCallback(
    formList => {
      return formList.reduce((acc, curr) => {
        const fee = getFormFees(curr);
        return acc + fee;
      }, 0);
    },
    [getFormFees]
  );

  const validateFormData = async () => {
    const getValidationRules = (formData, index) => {
      return [
        {
          key: "regular_class_id",
          test: () => {
            return {
              isValid: !regularClass.isUnassigned(formData.regular_class_id),
              message: `クラス${index + 1}の所属クラスを選択してください`,
            };
          },
        },
        {
          key: "times",
          test: () => {
            return {
              isValid: !!formData.times,
              message: `クラス${index + 1}の回数を選択してください`,
            };
          },
        },
        {
          key: "days",
          test: () => {
            let invalidPos = null;
            return {
              isValid:
                !regularClass.isDayTimeRequired(formData.regular_class_id) ||
                createArrayFromSize(formData.times).every(pos => {
                  const key = `day_week_${pos}`;
                  const isValid = !!formData[key];
                  if (!isValid) invalidPos = pos;
                  return isValid;
                }),
              message: `クラス${index + 1}の曜日${invalidPos}を選択してください`,
            };
          },
        },
        {
          key: "dayTime",
          test: () => {
            let invalidPos = null;
            return {
              isValid:
                !regularClass.isDayTimeRequired(formData.regular_class_id) ||
                createArrayFromSize(formData.times).every(pos => {
                  const key = `class_level_${pos}`;
                  const isValid = !!formData[key];
                  if (!isValid) invalidPos = pos;
                  return isValid;
                }),
              message: `クラス${index + 1}の時間${invalidPos}を選択してください`,
            };
          },
        },
        {
          key: "billingAmount",
          test: () => {
            return {
              isValid: billingAmount !== "",
              message: `請求額を入力してくださいください`,
            };
          },
        },
      ];
    };

    for (let [index, formData] of formList.entries()) {
      const validationRules = getValidationRules(formData, index);

      for (const rule of validationRules) {
        const testResult = rule.test(); // Call the test function
        if (!testResult.isValid) {
          setSelectedClassId(formData.id);
          await swalError(testResult.message); // Show swal error
          return false; // Stop validation and return false
        }
      }
    }

    return true;
  };

  const getStartDate = currentMonth => {
    if (!currentMonth) return null;
    let date = subtractMonths(currentMonth, 6);
    return dateFormat(date, 3);
  };

  const updateFormList = useCallback(
    async (formData, formList, type, selectedId = null) => {
      if (type === "create") {
        setFormList(formList);
        setBillingAmount(formList[0]?.billing_amount || getTotalFees(formList));
        setSelectedClassId(selectedId || formList[0]?.id || null);
        setDate(formList[0]?.processed_date);
      } else if (type.includes("add")) {
        setFormList(formList);
        setBillingAmount(getTotalFees(formList));
        setSelectedClassId(
          formList.at(type === "addFirst" ? 0 : -1).id || null
        );
        setDate(formList[0]?.processed_date);
        tableFilterButtonRef.current.click();
      } else if (type === "delete") {
        const newFormList = formList.filter(val => val.id !== formData.id);
        setFormList(newFormList);
        setBillingAmount(
          newFormList[0]?.billing_amount || getTotalFees(newFormList)
        );
        setSelectedClassId(newFormList.at(-1)?.id || null);
        tableFilterButtonRef.current.click();
      } else if (type === "update") {
        const newFormList = formList.map(val =>
          val.id === formData.id ? formData : val
        );
        setFormList(newFormList);
        setBillingAmount(getTotalFees(newFormList));
        setSelectedClassId(formData.id);
        setDate(formData.processed_date);
      } else {
        return null;
      }
    },
    [getTotalFees]
  );

  const memberClassesAssignMutation = usePostMemberClassesAssignMutation({
    mutationConfig: {
      onSuccess: async (data, variables) => {
        if (data && data.data && data.data.length > 0) {
          toast.success(data.message);
          const newFormList = generateClassFormListInfo(data.data);
          updateFormList(
            null,
            newFormList,
            variables.tryCopy ? "addFirst" : "addLast"
          );
        }
      },
      onError: async error => {
        await swalError(error.message);
      },
    },
  });

  //TODO: remove this functions refernce from the formstabs page, its no longer needed
  const handleAddClass = async (member_id, processed_date, tryCopy) => {
    const payload = {
      member_id,
      processed_date,
      try_copy: tryCopy ? 1 : 0,
    };

    memberClassesAssignMutation.mutate(payload);
  };

  useEffect(() => {
    if (currentMonth) {
      setDate(currentMonth);
    }
  }, [currentMonth]);

  const contextValues = {
    formList,
    setSelectedClassId,
    billingAmount,
    setBillingAmount,
    selectedClassId,
    totalFees: getTotalFees(formList),
    maxAllowedClasses,
    disableForm,
    handleAddClass,
    memberData,
    date,
    tableFilterButtonRef,
    currentMonth,
    classList,
    getStartDate,
    setClassList,
    updateFormList,
    readOnly,
    setReadOnly,
    formData,
    validateFormData,
    getFormFees,
  };

  return (
    <StudentClassContext.Provider value={contextValues}>
      {children}
    </StudentClassContext.Provider>
  );
};

export default StudentClassProvider;
